import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface Slide {
  image: string;
  alt: string;
  overlay: string;
  objectPosition: string;
}

const slides: Slide[] = [
  {
    image: 'https://storage.helloreaddy.io/project_files/fbab44cd-04c6-492b-a442-ed8f359ae362/de4edd5d-ae3e-4b27-af29-3cc5189d7cff_compressed_1.webp',
    alt: '白木のカウンターで職人が握る江戸前寿司',
    overlay: 'bg-gradient-to-b from-black/25 via-black/15 to-black/35',
    objectPosition: 'center',
  },
  {
    image: 'https://storage.helloreaddy.io/project_files/fbab44cd-04c6-492b-a442-ed8f359ae362/fbd5f8af-5502-4e4a-aaac-4c2faa39e5f6_compressed_4.webp',
    alt: '最高級の寿司シャリ',
    overlay: 'bg-gradient-to-t from-black/70 via-black/30 to-transparent',
    objectPosition: 'center',
  },
  {
    image: 'https://storage.helloreaddy.io/project_files/fbab44cd-04c6-492b-a442-ed8f359ae362/8ac5deed-4a2f-4a09-a257-b082e0318620_compressed_5.webp',
    alt: '宝石のような輝きのいくら',
    overlay: 'bg-gradient-to-t from-black/55 via-black/20 to-transparent',
    objectPosition: 'center',
  },
];

export default function HeroSection() {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const heroBoxRef = useRef<HTMLDivElement>(null);
  const [heroInView, setHeroInView] = useState(false);

  const startInterval = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length);
    }, 4000);
  }, []);

  useEffect(() => {
    startInterval();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [startInterval]);

  useEffect(() => {
    const el = heroBoxRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setHeroInView(entry.isIntersecting),
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const goToSlide = (index: number) => {
    setActiveIndex(index);
    startInterval();
  };

  return (
    <section className="relative w-full h-screen min-h-[700px] overflow-hidden">
      {/* Background Images */}
      {slides.map((slide, idx) => (
        <div
          key={idx}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            activeIndex === idx ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={slide.image}
            alt={slide.alt}
            className="w-full h-full object-cover"
            style={{ objectPosition: slide.objectPosition }}
          />
          <div className={`absolute inset-0 ${slide.overlay}`} />
        </div>
      ))}

      {/* Fixed centered text overlay with blur box */}
      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-4 md:px-6 w-full">
        <div
          ref={heroBoxRef}
          className={`hero-fade ${heroInView ? 'animate' : ''} border border-white/40 px-6 py-10 md:px-16 md:py-20 bg-black/40 flex flex-col items-center`}
        >
          <p className="font-heading text-base md:text-lg tracking-[0.1em] md:tracking-[0.3em] mb-4 md:mb-6 opacity-90 text-white drop-shadow-lg whitespace-nowrap">
            {t('hero_subtitle')}
          </p>
          <h1 className="font-calligraphy [writing-mode:vertical-rl] text-5xl md:text-7xl lg:text-8xl leading-tight tracking-wide text-white drop-shadow-xl">
            {t('hero_title')}
          </h1>
        </div>
      </div>

      {/* Slide Indicators & Scroll Hint */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-4">
        <div className="flex gap-2">
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goToSlide(idx)}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                activeIndex === idx ? 'bg-white w-6' : 'bg-white/40 w-1.5'
              }`}
              aria-label={`スライド ${idx + 1} へ`}
            />
          ))}
        </div>
        <span className="text-xs tracking-widest text-white/60">{t('scroll')}</span>
      </div>
    </section>
  );
}